import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import { injectIntl, FormattedMessage } from 'react-intl';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Box from '@mui/material/Box';

import messages from './messagesSleeperSupplements';
import { formatPrice } from '../../../../utils';
import styles from './styles';
import SleeperSupplementTravelSegment from './SleeperSupplementTravelSegment';
import { isSingleCabin } from './SleeperSupplementUtils';

const StyledAccordionSummary = styled(AccordionSummary)(
	() => styles.accordionSummary,
);

const SleeperSupplementsView = ({
	faresSelected,
	sleeperSupplements,
	passengerNumber,
	intl,
	onCheckSleeperSupplement,
	selectedSleeperSupplements,
	requestConnectingDoorsByLeg,
	setRequestConnectingDoorsByLeg,
}) => {
	const legs = faresSelected.toJS().map((item) => item.leg);
	const travelSegments = faresSelected
		.toJS()
		.map((item) => item.leg.ShoppingLegSolutions[0].ShoppingFareTravelSegments);

	const [expanded, setExpanded] = useState(legs[0].ShoppingLegId);

	const handleSummaryClick = (legID) => (event, expandedValue) => {
		setExpanded(expandedValue && legID);
	};

	const { formatMessage } = intl;

	const lblLeg = formatMessage(messages.lblLeg);
	const lblTo = formatMessage(messages.lblTo);
	const expandable = sleeperSupplements && sleeperSupplements.length > 0;

	// Split supplements into individual entries per travel segment and group by description
	const processedSleeperSupplements = sleeperSupplements
		.flatMap((supplement) =>
			supplement.linkedTravelSegments.map((segmentId) => {
				const segmentAvailability = supplement.supplementAvailabilities.find(
					(avail) => avail.travelSegmentID === segmentId,
				);

				if (!segmentAvailability || segmentAvailability.quantity === 0) {
					return null;
				}

				const price = parseFloat(supplement.value);
				return {
					...supplement,
					id: `${supplement.id}_${segmentId}`,
					originalId: supplement.id,
					linkedTravelSegments: [segmentId],
					supplementAvailabilities: [segmentAvailability],
					priceDesc: formatPrice(price, supplement.currency, intl),
					value: price,
					numberOfPassengers: passengerNumber,
				};
			}),
		)
		.filter(Boolean)
		.sort((a, b) => a.value - b.value);

	const doesSleeperSupplementExist = (legid) =>
		travelSegments[legid].some((segment) =>
			processedSleeperSupplements.some((supplement) =>
				supplement.linkedTravelSegments.includes(segment.TravelSegmentID),
			),
		);

	// Determine if connecting doors checkbox should be shown for a leg
	const shouldShowConnectingDoorsForLeg = (legid) => {
		const travelSegmentId = travelSegments[legid]?.[0]?.TravelSegmentID;
		if (!travelSegmentId) return false;

		const selectedSupplementsForLeg = selectedSleeperSupplements.filter(
			(supplement) =>
				supplement.linkedTravelSegments?.includes(travelSegmentId),
		);

		if (selectedSupplementsForLeg.length === 0) {
			return false;
		}

		// 1 passenger: Don't show
		if (passengerNumber === 1) {
			return false;
		}

		// For 3+ passengers: ALWAYS show checkbox
		if (passengerNumber >= 3) {
			return true;
		}

		// For 2 passengers: Show ONLY when BOTH passengers select single cabins
		if (passengerNumber === 2) {
			if (selectedSupplementsForLeg.length !== 2) {
				return false;
			}

			const bothAreSingleCabins =
				selectedSupplementsForLeg.every(isSingleCabin);

			return bothAreSingleCabins;
		}

		return false;
	};

	const handleConnectingDoorsChange = (travelSegmentId) => (event) => {
		setRequestConnectingDoorsByLeg({
			...requestConnectingDoorsByLeg,
			[travelSegmentId]: event.target.checked,
		});
	};

	return (
		<div className="container-fluid" key="sleeperSupplements-Dialog">
			{legs.map(
				(leg, legid) =>
					doesSleeperSupplementExist(legid) && (
						<Accordion
							key={`ExpansionPanel${legid}`}
							expanded={expanded === leg.ShoppingLegId}
							onChange={
								expandable ? handleSummaryClick(leg.ShoppingLegId) : undefined
							}
						>
							{travelSegments[legid] && travelSegments[legid].length >= 1 && (
								<StyledAccordionSummary
									expandIcon={expandable && <ExpandMoreIcon />}
								>
									{`${lblLeg} ${legid + 1}: ${leg.ShoppingLegOrigStationName} ${lblTo} ${leg.ShoppingLegDestStationName}`}
								</StyledAccordionSummary>
							)}
							<AccordionDetails key={`ExpansionPanelDetails${legid}`}>
								<Grid container>
									<SleeperSupplementTravelSegment
										key={`SleeperSupplementTravelSegment${legid}`}
										legid={legid}
										travelSegments={travelSegments}
										searchedSleeperSupplements={processedSleeperSupplements}
										onCheckSleeperSupplement={onCheckSleeperSupplement}
										selectedSleeperSupplements={selectedSleeperSupplements}
										passengerNumber={passengerNumber}
									/>
									{shouldShowConnectingDoorsForLeg(legid) && (
										<Grid item xs={12}>
											<Box
												sx={{
													mt: 2,
													pt: 2,
													borderTop: '1px solid',
													borderColor: 'divider',
												}}
											>
												<FormControlLabel
													control={
														<Checkbox
															checked={
																requestConnectingDoorsByLeg[
																	travelSegments?.[legid]?.[0]?.TravelSegmentID
																] || false
															}
															onChange={handleConnectingDoorsChange(
																travelSegments?.[legid]?.[0]?.TravelSegmentID,
															)}
															color="primary"
														/>
													}
													label={
														<FormattedMessage
															{...messages.connectingDoorsCheckbox}
														/>
													}
												/>
											</Box>
										</Grid>
									)}
								</Grid>
							</AccordionDetails>
						</Accordion>
					),
			)}
		</div>
	);
};

SleeperSupplementsView.propTypes = {
	intl: PropTypes.object,
	onCheckSleeperSupplement: PropTypes.func.isRequired,
	faresSelected: PropTypes.object.isRequired,
	sleeperSupplements: PropTypes.array.isRequired,
	passengerNumber: PropTypes.number.isRequired,
	selectedSleeperSupplements: PropTypes.array,
	requestConnectingDoorsByLeg: PropTypes.object.isRequired,
	setRequestConnectingDoorsByLeg: PropTypes.func.isRequired,
};

export { SleeperSupplementsView as SleeperSupplementsViewAlias };

export default injectIntl(SleeperSupplementsView);
